import React from "react";
import {observer} from "mobx-react-lite";
import Drawer from "@mui/material/Drawer";
import {useStores} from "~/stores";
import {styled} from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListSubheader,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {generatePath} from "react-router";
import {AdminPanelSettings, ChevronRight, DeveloperBoard, Error, Launch} from "@mui/icons-material";
import {CARD_CONFIG} from "../../dashboard/DashboardCardConfigs.tsx";
import {routes} from "~/routing/routes.ts";
import type {ColumnConfiguration} from "~/stores/config/DatabagConfigClasses.ts";
import {MLink} from "../../_tiny/MLink.tsx";
import {B, projectIdWithFallback} from "~/build.ts";
import {STAFF_DEVOPS_CENTER_URL} from "~/constants.ts";

const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

export const DevopsMenu: React.FC = observer(() => {
    const {authStore, rootAppStore, bagStore} = useStores();

    if (!authStore.isMithraStaffGroup) {
        return <></>;
    }

    const columns: ColumnConfiguration[] | undefined = (bagStore.apiColumns === undefined) ? undefined : [...bagStore.apiColumns];
    if (columns) {
        // sort by table_name, then by column_name
        columns.sort((a, b) => `${a.table_name}|${a.column_name}`.localeCompare(`${b.table_name}|${b.column_name}`));
    }

    return <>
        <IconButton
            edge="end"
            size="medium"
            aria-haspopup="true"
            color="inherit"
            onClick={() => rootAppStore.setIsDevopsMenuOpen(true)}>
            <DeveloperBoard/>
        </IconButton>
        <Drawer
            anchor="right"
            hideBackdrop
            variant="persistent"
            open={rootAppStore.isDevopsMenuOpen}
            onClose={() => rootAppStore.setIsDevopsMenuOpen(false)}>
            <DrawerHeader>
                <AdminPanelSettings/>
                <IconButton onClick={() => rootAppStore.setIsDevopsMenuOpen(false)}>
                    <ChevronRight/>
                </IconButton>
            </DrawerHeader>

            <Divider/>
            <List>
                <ListSubheader>Links</ListSubheader>
                {B.STAFF_MANAGEMENT_URL && <ListItem disablePadding>
                    <ListItemButton>
                        {/* Same look as the MLink entries below, but external and in a new tab */}
                        <a className="mithra-link" href={B.STAFF_MANAGEMENT_URL} target="_blank"
                           rel="noopener noreferrer">
                            <ListItemText primary={<><Launch/> Staff management</>}/>
                        </a>
                    </ListItemButton>
                </ListItem>}
                <ListItem disablePadding>
                    <ListItemButton>
                        <a className="mithra-link" href={
                            STAFF_DEVOPS_CENTER_URL + '?project=' + projectIdWithFallback()
                        } target="_blank"
                           rel="noopener noreferrer">
                            <ListItemText primary={<><Launch/> Operations Center</>}/>
                        </a>
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton>
                        <MLink path={generatePath(routes[CARD_CONFIG['data-ingestion'].route])}>
                            <ListItemText primary={<><Launch/> {CARD_CONFIG['data-ingestion'].title}</>}/>
                        </MLink>
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton>
                        <MLink path={generatePath(routes[CARD_CONFIG['taxonomy-ingestion'].route])}>
                            <ListItemText primary={<><Launch/> {CARD_CONFIG['taxonomy-ingestion'].title}</>}/>
                        </MLink>
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton>
                        <MLink path={generatePath(routes[CARD_CONFIG['sit'].route])}>
                            <ListItemText primary={<><Launch/> {CARD_CONFIG['sit'].title}</>}/>
                        </MLink>
                    </ListItemButton>
                </ListItem>

                <ListSubheader>Chat</ListSubheader>
                <ListItem disablePadding>
                    <ListItemButton disabled={bagStore.bagId === 0}>
                        <MLink path={generatePath(routes[CARD_CONFIG['chat'].route], {id: bagStore.bagId})}
                               disabled={bagStore.bagId === 0}>
                            <ListItemText primary={<><Launch/> {CARD_CONFIG['chat'].title}</>}/>
                        </MLink>
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <TextField
                        label="Locale [customer-config]"
                        disabled
                        fullWidth
                        variant="filled"
                        value={'' + bagStore.customerConfigLocale}
                    />
                    <TextField
                        label="Currency [customer-config]"
                        disabled
                        fullWidth
                        variant="filled"
                        value={'' + bagStore.customerConfigCurrency}
                    />
                </ListItem>
                <ListItem disablePadding>
                    <Accordion>
                        <AccordionSummary>
                            {bagStore.apiColumns?.length === 0 && <Error color="error"/>}
                            <Typography component="span">
                                Columns used in chat
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ul>
                                {columns?.map((col, index) => (
                                    <li key={index}>
                                        <Tooltip title={col.description}>
                                            <span>{col.table_name}.{col.column_name} [{col.display_name}]</span>
                                        </Tooltip>
                                    </li>
                                ))}
                                {bagStore.apiColumns?.length === 0 &&
                                    <li>No chat enabled columns found!</li>
                                }
                            </ul>
                        </AccordionDetails>
                    </Accordion>
                </ListItem>

                <ListSubheader>Config</ListSubheader>
                <ListItem disablePadding>
                    <Collapse in={rootAppStore.isDevopsMenuOpen}
                              style={{width: '100%'}}>{/*Fixes label-bug on multiline*/}
                        <TextField
                            label="Databag"
                            disabled
                            variant="filled"
                            multiline
                            maxRows={4}
                            fullWidth
                            value={JSON.stringify(bagStore.bag, null, 2)}
                        />
                    </Collapse>
                </ListItem>

                <ListItem disablePadding>
                    {!bagStore.customerConfig && <ListItemText primary="No customer config loaded"/>}
                    {bagStore.customerConfig &&
                        <List subheader="Customer Config">
                            {Array.from(bagStore.customerConfig.values()).map(c => (
                                <ListItem key={c.key} disablePadding>
                                    <ListItemText
                                        primary={c.key}
                                        secondary={JSON.stringify(c.value)}
                                    />
                                </ListItem>
                            ))}
                            {bagStore.customerConfig.size === 0 &&
                                <ListItemText primary="No customer config entries found"/>}
                        </List>
                    }
                </ListItem>
                <ListItem disablePadding>
                    <Collapse in={rootAppStore.isDevopsMenuOpen}
                              style={{width: '100%'}}>{/*Fixes label-bug on multiline*/}
                        <TextField
                            label="Databag config (excluding columns)"
                            disabled
                            variant="filled"
                            multiline
                            maxRows={4}
                            fullWidth
                            value={JSON.stringify({
                                ...bagStore.databagConfig,
                                columns: undefined,
                                databag_id: undefined
                            })}
                        />
                    </Collapse>
                </ListItem>
                <ListItem disablePadding>
                    <Collapse in={rootAppStore.isDevopsMenuOpen}
                              style={{width: '100%'}}>{/*Fixes label-bug on multiline*/}
                        <TextField
                            label="Databag config.columns"
                            disabled
                            variant="filled"
                            multiline
                            maxRows={4}
                            fullWidth
                            value={JSON.stringify(bagStore.databagConfig?.columns)}
                        />
                    </Collapse>
                </ListItem>

            </List>
        </Drawer>

    </>
})
